import { FC, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { EmployeeModalContent } from './EditorModalContent';
import { useToastContext } from '../../hooks';
import { EmployeesAPI } from '../../fetch/employee';
import { IEmployeeDetail, ILookupType } from '../../models/employee';
import { Loader } from '../../components';
// fetch

interface IEmployeeEditorModal {
  open: boolean;
  onClose: () => void;
  onCloseAfterSave: () => void;
  employeeId: string;
  areModalDropdownValuesLoading: boolean;
  subsidiaries: ILookupType[];
  roles: ILookupType[];
  offices: ILookupType[];
  activityTypes: ILookupType[];
  productSpecialties: ILookupType[];
  teams: ILookupType[];
  primaryBusinessUnitOverride: ILookupType[];
  departmentOverride: ILookupType[];
  productTypeOverride: ILookupType[];
  commissionTables: ILookupType[];
  specialtyTypes: ILookupType[];
  bankAccounts: ILookupType[];
}

const Schema = Yup.object().shape({
  subsidiaryId: Yup.number(),
  bankAccountId: Yup.number(),
  roles: Yup.array().of(Yup.number()),
  offices: Yup.array().of(Yup.number()),
  activityTypes: Yup.array().of(Yup.number()),
  primaryProductSpecialtyType: Yup.number(),
  netsuiteId: Yup.number(),
  teams: Yup.array().of(Yup.number()),
  primaryTeam: Yup.number().required(),
  assistants: Yup.array().of(
    Yup.object().shape({ employeeId: Yup.number(), isPrimary: Yup.boolean() })
  ),
  agents: Yup.array().of(Yup.number()),
  supportStaff: Yup.array().of(
    Yup.object().shape({ employeeId: Yup.number()})
  ),
  supportStaffAgents: Yup.array().of(Yup.number()),
  import: Yup.boolean(),
  export: Yup.boolean(),
  deletePerson: Yup.boolean(),
  deleteProperty: Yup.boolean(),
  includeInDirectory: Yup.boolean(),
  dynamicFieldsAdmin: Yup.boolean(),
  assignedCredit: Yup.number()
  .required('SendGrid Credits is required')
});

export enum IMPORTANT_ROLES {
  ASSISTANT = 3,
  PRODUCER = 4,
  SUPPORT_STAFF = 13
}

export const EmployeeEditorModal: FC<IEmployeeEditorModal> = ({
  open,
  onClose,
  onCloseAfterSave,
  employeeId,
  areModalDropdownValuesLoading,
  subsidiaries,
  roles,
  offices,
  activityTypes,
  productSpecialties,
  teams,
  primaryBusinessUnitOverride,
  departmentOverride,
  productTypeOverride,
  commissionTables,
  specialtyTypes,
  bankAccounts,
}) => {
  const { showToast } = useToastContext();

  const [isLoading, setIsLoading] = useState<boolean>();
  const [currentEmployee, setCurrentEmployee] = useState<IEmployeeDetail>();

  const fetchEmployeeData = async () => {
    setIsLoading(true);
    try {
      const response = await EmployeesAPI.fetchEmployee(employeeId);
      setCurrentEmployee(response);
    } catch (error: any) {
      showToast('error', error?.detail ?? 'Error getting news item.');
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (employeeId) {
      fetchEmployeeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, open]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          subsidiaryId: currentEmployee?.subsidiaryId ?? '',
          bankAccountId: currentEmployee?.bankAccountId ?? '',
          roles: currentEmployee?.roles ?? [],
          offices: currentEmployee?.offices ?? [],
          activityTypes: currentEmployee?.activityTypes ?? [],
          primaryProductSpecialtyType: currentEmployee?.primaryProductSpecialtyType ?? '',
          netsuiteId: currentEmployee?.netsuiteId ?? '',
          primaryBusinessUnitOverride: currentEmployee?.primaryBusinessUnitOverride ?? '',
          departmentOverride: currentEmployee?.departmentOverride ?? '',
          primaryProductTypeOverride: currentEmployee?.primaryProductTypeOverride ?? '',
          primarySpecialtyTypeOverride: currentEmployee?.primarySpecialtyTypeOverride ?? '',
          isAdActive: currentEmployee?.isAdActive ?? false,
          teams: currentEmployee?.teams ?? [],
          primaryTeam: currentEmployee?.primaryTeam ?? '',

          assistants: currentEmployee?.assistants ?? [],
          agents: currentEmployee?.agents ?? [],
          supportStaff: currentEmployee?.supportStaff ?? [],
          // SupportStaffAgents must exclude any IDs already in agents (mutually exclusive sets)
          supportStaffAgents: (currentEmployee?.supportStaffAgents || []).filter(id => !(currentEmployee?.agents || []).includes(id)),
          canImport: currentEmployee?.canImport ?? false,
          canExport: currentEmployee?.canExport ?? false,
          canImportPropertyFinancials: currentEmployee?.canImportPropertyFinancials ?? false,
          canDeletePerson: currentEmployee?.canDeletePerson ?? false,
          canDeleteProperty: currentEmployee?.canDeleteProperty ?? false,
          shouldIncludeInDirectory: currentEmployee?.shouldIncludeInDirectory ?? false,
          isDynamicFieldsAdmin: currentEmployee?.isDynamicFieldsAdmin ?? false,
          canMergeCompany: currentEmployee?.canMergeCompany ?? false,
          commissionTable: currentEmployee?.commissionTable ?? '',
          canBulkEmail: currentEmployee?.canBulkEmail ?? false,
          assignedCredit: currentEmployee?.assignedCredit ?? 500, // default credit 500
          protectedClients:
            currentEmployee?.protectedClients && currentEmployee?.protectedClients.length > 0
              ? currentEmployee?.protectedClients
              : [
                  {
                    protectedClientToEmployeeId: 0,
                    clientId: undefined,
                    clientName: '',
                    startDate: '',
                    endDate: '',
                    isActive: true,
                  },
                ],
          marginCategories:
            currentEmployee?.marginCategories && currentEmployee?.marginCategories.length > 0
              ? currentEmployee?.marginCategories
              : [
                  {
                    employeeMarginCategoryId: 0,
                    marginCategoryTypeId: 0,
                    effectiveDate: '',
                    isDeleted: false,
                  },
                ],
        }}
        validationSchema={Schema}
        onSubmit={async (values, actions) => {
          const data = {
            employeeId: employeeId,
            body: {
              ...values,
              agents: values.roles.includes(IMPORTANT_ROLES.ASSISTANT) ? values.agents : [],
              assistants: values.roles.includes(IMPORTANT_ROLES.PRODUCER) ? values.assistants : [],
              supportStaff: values.roles.includes(IMPORTANT_ROLES.PRODUCER) ? values.supportStaff : [],
              // For Support Team role (formerly Support Staff), persist the dedicated supportStaffAgents collection (new UI under feature flag)
              supportStaffAgents: values.roles.includes(IMPORTANT_ROLES.SUPPORT_STAFF) ? values.supportStaffAgents : [],
              primaryTeam: values.teams.includes(+values.primaryTeam) ? values.primaryTeam : null,
              protectedClients: values.protectedClients
                .filter(client => client.clientName)
                .map(client => {
                  return {
                    ...client,
                    clientId: client.clientId,
                    clientName: client.clientName,
                    protectedClientToEmployeeId: client.protectedClientToEmployeeId,
                    startDate: client.startDate,
                    endDate: client.endDate,
                    isActive: client.isActive,
                  };
                }),
              marginCategories: values.marginCategories.filter(
                category => category.marginCategoryTypeId
              ),
            },
          };
          setIsLoading(true);
          try {
            await EmployeesAPI.updateEmployee(employeeId, data);
            onCloseAfterSave();
            setCurrentEmployee(undefined);

            actions.resetForm();
          } catch (error: any) {
            showToast('error', error?.detail ?? 'Error saving Employee, please try again.');
          } finally {
            setIsLoading(false);
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          setFieldValue,
          handleSubmit,
          dirty,
          isValid,
          initialValues,
          errors,
          touched,
    
        }) => {
          const handleClose = () => {
            if (!deepEqual(initialValues, values)) {
              const result = window.confirm(
                'You have unsaved changes, are you sure you want to exit?'
              );
              if (result) {
                resetForm();
                setCurrentEmployee(undefined);

                onClose();
              } else {
                return;
              }
            } else {
              onClose();
              setCurrentEmployee(undefined);
            }
          };
          return open && !isLoading ? (
            <EmployeeModalContent
              employeeId={employeeId}
              open={open}
              handleClose={handleClose}
              setFieldValue={setFieldValue}
              values={values}
              handleSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              dirty={dirty}
              isValid={isValid}
              touched={touched}
              currentEmployee={currentEmployee}
              subsidiaries={subsidiaries}
              bankAccounts={bankAccounts}
              roles={roles}
              offices={offices}
              activityTypes={activityTypes}
              specialtyTypes={specialtyTypes}
              productSpecialties={productSpecialties}
              teams={teams}
              errors={errors}
              primaryBusinessUnitOverride={primaryBusinessUnitOverride}
              departmentOverride={departmentOverride}
              productTypeOverride={productTypeOverride}
              commissionTables={commissionTables}
              fetchEmployeeData={fetchEmployeeData}
              isAdActiveMessage={
                currentEmployee?.overrideStatus
                  ? currentEmployee?.overrideStatus
                  : 'Status is maintained by Entra'
              }
            />
          ) : (
            isLoading && <Loader type="overlay" position="centered" />
          );
        }}
      </Formik>
    </>
  );
};
